/**
 * A meeting row's actions and their dialogs (TMMAPP-0103; folded by TMMAPP-0108 so My Meetings and the
 * virtual account's Meetings & summaries tab share one copy): open the session, the editor (read-only
 * for a viewer who can't write), the calendar file, start, and the confirmations — cancel one
 * occurrence, end a live one, cancel the series, end it from a date. Ending and cancelling can't be
 * undone, so each confirms first (TMM-B4). The caller says who writes on a virtual account's
 * meeting; the hook never decides that.
 *
 * A standalone meeting (TMMAPP-0159 §D 23, no virtual account) follows its own served rules: the
 * definition, its cancels and its end-from are the organizer's alone, so no one else is offered them;
 * start and end are every writer's (`canWriteStandalone`).
 */
import { useState, type ReactNode } from 'react';
import { TextField } from '@mui/material';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  meetingsApi,
  type Meeting,
  type MeetingCalendarEntry,
  type MeetingHome,
} from '@/api/meetings';
import { ConfirmDialog } from '@/components/ConfirmDialog';
import { useConfirm } from '@/components/useConfirm';
import { addCivilDays } from '@/shared/servedWindow';
import { useMe } from '@/session/meHooks';
import { useNotify } from '@/shell/NotifyProvider';
import { todayCivilInZone } from '@/i18n/zonedCivil';
import { downloadBlob } from '@/utils/downloadBlob';
import { slug } from '@/utils/slug';
import { MeetingEditorDialog, type VirtualAccountChoice } from './MeetingEditorDialog';
import type { MeetingRowActions } from './MeetingRow';
import { isPastEntry, nowMs } from './meetingLists';
import { meetingSessionPath } from './nextMeeting';
import { canWriteStandalone, organizesEntry } from './standalone';

type EditorState =
  | {
      kind: 'new';
      virtualAccountId?: number;
      choices?: readonly VirtualAccountChoice[];
      standalone?: boolean;
    }
  | {
      kind: 'edit';
      meeting: Meeting;
      virtualAccountId: MeetingHome;
      readOnly: boolean;
      past: boolean;
    };

interface UseMeetingActionsOptions {
  /** Whether the viewer writes meetings on this entry's virtual account (never asked standalone). */
  writer: (entry: MeetingCalendarEntry) => boolean;
  /** Refetch the list after a change. */
  reload: () => Promise<void>;
}

export interface MeetingActions {
  actionsFor: (entry: MeetingCalendarEntry) => MeetingRowActions;
  /** The title and the date badge: the meeting editor. */
  openEditor: (entry: MeetingCalendarEntry) => Promise<void>;
  /**
   * "+ New meeting": the editor on a virtual account, or with a picker of the viewer's own and, with
   * `standalone`, the choice of a Just us meeting (TMMAPP-0159 §D 20).
   */
  openNew: (
    target:
      | { virtualAccountId: number }
      | { choices: readonly VirtualAccountChoice[]; standalone?: boolean },
  ) => void;
  /** The editor and every confirmation; render it once beside the list. */
  node: ReactNode;
}

export function useMeetingActions({ writer, reload }: UseMeetingActionsOptions): MeetingActions {
  const { t } = useTranslation();
  const me = useMe();
  const navigate = useNavigate();
  const notify = useNotify();
  const zone = me.user.timezone ?? 'UTC';
  const tomorrow = addCivilDays(todayCivilInZone(zone), 1);
  const [editor, setEditor] = useState<EditorState | null>(null);
  const cancelling = useConfirm<MeetingCalendarEntry>();
  const endFrom = useConfirm<MeetingCalendarEntry>();
  const occurrenceConfirm = useConfirm<{
    action: 'cancel' | 'end';
    entry: MeetingCalendarEntry;
  }>();
  const [endDate, setEndDate] = useState('');

  /** The organizer's alone on a standalone meeting; the virtual account's writers' otherwise. */
  const defines = (entry: MeetingCalendarEntry) =>
    entry.virtualAccountId === null ? organizesEntry(entry, me.user.id) : writer(entry);
  /** Start and end: every writer's. */
  const runs = (entry: MeetingCalendarEntry) =>
    entry.virtualAccountId === null ? canWriteStandalone(me, defines(entry)) : writer(entry);

  async function openEditor(entry: MeetingCalendarEntry) {
    const virtualAccountId = entry.virtualAccountId;
    try {
      const meeting = await meetingsApi.get(virtualAccountId, entry.meetingId);
      setEditor({
        kind: 'edit',
        meeting,
        virtualAccountId,
        readOnly:
          virtualAccountId === null
            ? me.user.id === null || meeting.organizer.userId !== me.user.id
            : !writer(entry),
        past: isPastEntry(entry, nowMs()),
      });
    } catch {
      notify(t('meetings.errorGeneric'), { severity: 'error' });
    }
  }

  async function runOccurrence(
    action: 'cancel' | 'start' | 'end',
    entry: MeetingCalendarEntry,
    rethrow = false,
  ) {
    const virtualAccountId = entry.virtualAccountId;
    try {
      if (action === 'cancel')
        await meetingsApi.cancelOccurrence(virtualAccountId, entry.meetingId, entry.occurrenceKey);
      if (action === 'start')
        await meetingsApi.start(virtualAccountId, entry.meetingId, entry.occurrenceKey);
      if (action === 'end')
        await meetingsApi.end(virtualAccountId, entry.meetingId, entry.occurrenceKey);
      notify(
        action === 'cancel'
          ? t('meetings.occurrenceCancelled')
          : action === 'start'
            ? t('meetings.started')
            : t('meetings.ended'),
      );
      await reload();
    } catch (error) {
      if (rethrow) throw error;
      notify(t('meetings.errorGeneric'), { severity: 'error' });
    }
  }

  async function handleIcs(entry: MeetingCalendarEntry) {
    try {
      const { blob, filename } = await meetingsApi.ics(entry.virtualAccountId, entry.meetingId);
      downloadBlob(blob, filename ?? `${slug(entry.title, 'meeting')}.ics`);
      notify(t('meetings.icsStarted'));
    } catch {
      notify(t('meetings.errorGeneric'), { severity: 'error' });
    }
  }

  function actionsFor(entry: MeetingCalendarEntry): MeetingRowActions {
    const happened = isPastEntry(entry, nowMs());
    const scheduled = entry.status === 'SCHEDULED' && !happened;
    return {
      openSession: () => void navigate(meetingSessionPath(entry)),
      downloadIcs: () => void handleIcs(entry),
      ...(defines(entry)
        ? {
            edit: () => void openEditor(entry),
            cancelSeries: () => cancelling.ask(entry),
            endFrom: () => {
              endFrom.ask(entry);
              setEndDate(tomorrow);
            },
            ...(scheduled
              ? { cancelOccurrence: () => occurrenceConfirm.ask({ action: 'cancel', entry }) }
              : {}),
          }
        : {}),
      ...(runs(entry)
        ? {
            ...(scheduled && !entry.live
              ? { start: () => void runOccurrence('start', entry) }
              : {}),
            ...(entry.live ? { end: () => occurrenceConfirm.ask({ action: 'end', entry }) } : {}),
          }
        : {}),
    };
  }

  async function handleCancelSeries() {
    await cancelling.run(
      async (entry) => {
        await meetingsApi.cancelSeries(entry.virtualAccountId, entry.meetingId);
        notify(t('meetings.cancelled'));
        await reload();
      },
      () => t('meetings.errorGeneric'),
    );
  }

  async function handleEndFrom() {
    if (endDate < tomorrow) return;
    await endFrom.run(
      async (entry) => {
        await meetingsApi.endFrom(entry.virtualAccountId, entry.meetingId, endDate);
        notify(t('meetings.seriesEnded'));
        await reload();
      },
      () => t('meetings.errorGeneric'),
    );
  }

  const saved = (message: string) => {
    setEditor(null);
    notify(message);
    void reload();
  };

  const node = (
    <>
      {editor?.kind === 'new' && (
        <MeetingEditorDialog
          {...(editor.virtualAccountId !== undefined
            ? { virtualAccountId: editor.virtualAccountId }
            : { choices: editor.choices ?? [], allowStandalone: editor.standalone === true })}
          onClose={() => setEditor(null)}
          onSaved={saved}
        />
      )}
      {editor?.kind === 'edit' && (
        <MeetingEditorDialog
          virtualAccountId={editor.virtualAccountId}
          meeting={editor.meeting}
          readOnly={editor.readOnly}
          past={editor.past}
          onClose={() => setEditor(null)}
          onSaved={saved}
        />
      )}
      {occurrenceConfirm.target !== null && (
        <ConfirmDialog
          title={
            occurrenceConfirm.target.action === 'end'
              ? t('meetings.endTitle', { title: occurrenceConfirm.target.entry.title })
              : t('meetings.cancelOccurrenceTitle', { title: occurrenceConfirm.target.entry.title })
          }
          consequence={
            occurrenceConfirm.target.action === 'end'
              ? t('meetings.endConsequence')
              : t('meetings.cancelOccurrenceConsequence')
          }
          confirmLabel={
            occurrenceConfirm.target.action === 'end'
              ? t('meetings.end')
              : t('meetings.cancelOccurrence')
          }
          cancelLabel={
            occurrenceConfirm.target.action === 'end'
              ? t('meetings.keepOpen')
              : t('meetings.keepOccurrence')
          }
          destructive
          busy={occurrenceConfirm.busy}
          error={occurrenceConfirm.error}
          onConfirm={() =>
            void occurrenceConfirm.run(
              async ({ action, entry }) => {
                await runOccurrence(action, entry, true);
              },
              () => t('meetings.errorGeneric'),
            )
          }
          onCancel={occurrenceConfirm.cancel}
        />
      )}
      {cancelling.target !== null && (
        <ConfirmDialog
          title={t('meetings.cancelSeriesTitle', { title: cancelling.target.title })}
          consequence={t('meetings.cancelSeriesConsequence')}
          confirmLabel={t('meetings.cancelSeries')}
          cancelLabel={t('meetings.keepSeries')}
          destructive
          busy={cancelling.busy}
          error={cancelling.error}
          onConfirm={() => void handleCancelSeries()}
          onCancel={cancelling.cancel}
        />
      )}
      {endFrom.target !== null && (
        <ConfirmDialog
          title={t('meetings.endFromSeriesTitle', { title: endFrom.target.title })}
          consequence={t('meetings.endFromConsequence')}
          confirmLabel={t('meetings.endSeries')}
          destructive
          busy={endFrom.busy}
          error={endFrom.error}
          confirmDisabled={endDate < tomorrow}
          onConfirm={() => void handleEndFrom()}
          onCancel={endFrom.cancel}
        >
          <TextField
            type="date"
            label={t('meetings.endFromDate')}
            value={endDate}
            onChange={(event) => setEndDate(event.target.value)}
            disabled={endFrom.busy}
            error={endDate < tomorrow}
            helperText={endDate < tomorrow ? t('meetings.endFromPast') : undefined}
            slotProps={{ htmlInput: { min: tomorrow } }}
          />
        </ConfirmDialog>
      )}
    </>
  );

  return {
    actionsFor,
    openEditor,
    openNew: (target) =>
      setEditor({
        kind: 'new',
        ...('virtualAccountId' in target
          ? { virtualAccountId: target.virtualAccountId }
          : { choices: target.choices, standalone: target.standalone === true }),
      }),
    node,
  };
}
